import React, { useState } from 'react';
import { FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, CheckCircleIcon, ClockIcon, CalendarIcon, TruckIcon, BarChartIcon, PlusIcon, FilterIcon, ShoppingCartIcon, XIcon, CheckIcon, TagIcon, DollarSignIcon, PackageIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function ProcurementDashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const pendingPOs = purchaseOrders.filter(po => po.status === 'Pending Confirmation' || po.status === 'Draft').length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for procurement insights
  const pendingTasks = [{
    id: 'TASK-001',
    type: 'Requisition',
    name: 'Office Supplies Approval',
    dueDate: '2023-11-20',
    priority: 'High',
    status: 'Pending Approval'
  }, {
    id: 'TASK-002',
    type: 'Purchase Order',
    name: 'Server Hardware Confirmation',
    dueDate: '2023-11-18',
    priority: 'Medium',
    status: 'Pending Confirmation'
  }, {
    id: 'TASK-003',
    type: 'Receipt',
    name: 'IT Equipment Delivery',
    dueDate: '2023-11-25',
    priority: 'Low',
    status: 'Expected'
  }];
  const upcomingDeliveries = [{
    id: 'PO-2023-001',
    vendor: 'Server Solutions Inc.',
    items: 'Server Hardware',
    expectedDate: '2023-11-22',
    status: 'In Transit',
    trackingNumber: 'TRK123456789'
  }, {
    id: 'PO-2023-002',
    vendor: 'PrintWorks Inc.',
    items: 'Marketing Materials',
    expectedDate: '2023-11-25',
    status: 'Processing',
    trackingNumber: null
  }];
  const topSpendCategories = [{
    category: 'IT Equipment',
    spend: 12500,
    percentage: 40
  }, {
    category: 'Office Supplies',
    spend: 8000,
    percentage: 25
  }, {
    category: 'Marketing',
    spend: 6200,
    percentage: 20
  }, {
    category: 'Services',
    spend: 4800,
    percentage: 15
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-12579">
      <div className="mb-8 flex justify-between items-center" data-id="element-12580">
        <div data-id="element-12581">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-12582">
            Procurement Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-12583">
            Overview of requisitions, purchase orders, and goods receipts
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-12584">
          <div className="flex items-center" data-id="element-12585">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-12586" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-12587">
              <option value="7days" data-id="element-12588">Last 7 days</option>
              <option value="30days" data-id="element-12589">Last 30 days</option>
              <option value="90days" data-id="element-12590">Last 90 days</option>
              <option value="ytd" data-id="element-12591">Year to date</option>
            </select>
          </div>
          <button onClick={() => setCurrentView('create-requisition')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12592">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-12593" />
            New Requisition
          </button>
        </div>
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-12594">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-12595">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-12596">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-12597">
            <div className="p-5" data-id="element-12598">
              <div className="flex items-center" data-id="element-12599">
                <div className="flex-shrink-0" data-id="element-12600">
                  <FileTextIcon className="h-6 w-6 text-indigo-400" data-id="element-12601" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-12602">
                  <dl data-id="element-12603">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-12604">
                      Active Requisitions
                    </dt>
                    <dd data-id="element-12605">
                      <div className="text-lg font-medium text-gray-900" data-id="element-12606">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-12607">
            <div className="p-5" data-id="element-12608">
              <div className="flex items-center" data-id="element-12609">
                <div className="flex-shrink-0" data-id="element-12610">
                  <ClipboardCheckIcon className="h-6 w-6 text-blue-400" data-id="element-12611" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-12612">
                  <dl data-id="element-12613">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-12614">
                      Active Purchase Orders
                    </dt>
                    <dd data-id="element-12615">
                      <div className="text-lg font-medium text-gray-900" data-id="element-12616">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-12617">
            <div className="p-5" data-id="element-12618">
              <div className="flex items-center" data-id="element-12619">
                <div className="flex-shrink-0" data-id="element-12620">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-12621" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-12622">
                  <dl data-id="element-12623">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-12624">
                      Pending Approvals
                    </dt>
                    <dd data-id="element-12625">
                      <div className="text-lg font-medium text-gray-900" data-id="element-12626">
                        {pendingApproval + pendingPOs}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-12627">
            <div className="p-5" data-id="element-12628">
              <div className="flex items-center" data-id="element-12629">
                <div className="flex-shrink-0" data-id="element-12630">
                  <DollarSignIcon className="h-6 w-6 text-green-400" data-id="element-12631" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-12632">
                  <dl data-id="element-12633">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-12634">
                      Total Procurement Spend
                    </dt>
                    <dd data-id="element-12635">
                      <div className="text-lg font-medium text-gray-900" data-id="element-12636">
                        {formatCurrency(totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Procurement Metrics */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-12637">
        {/* Pending Tasks */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-12638">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-12639">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12640">
              Pending Tasks
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-12641">
              {pendingTasks.length} items
            </span>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-12642">
            {pendingTasks.map(task => <li key={task.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => {
            if (task.type === 'Requisition') {
              setCurrentView('requisitions');
            } else if (task.type === 'Purchase Order') {
              setCurrentView('purchase-orders');
            } else {
              setCurrentView('receipting');
            }
          }} data-id="element-12643">
                <div className="flex items-center justify-between" data-id="element-12644">
                  <div className="flex items-center" data-id="element-12645">
                    <div className="flex-shrink-0" data-id="element-12646">
                      {task.type === 'Requisition' ? <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-12647" /> : task.type === 'Purchase Order' ? <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-12648" /> : <TruckIcon className="h-5 w-5 text-green-400" data-id="element-12649" />}
                    </div>
                    <div className="ml-3" data-id="element-12650">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-12651">
                        {task.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-12652">
                        {task.id} • {task.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-12653">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority === 'High' ? 'bg-red-100 text-red-800' : task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-12654">
                      {task.priority}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-12655">
                      Due: {task.dueDate}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        {/* Upcoming Deliveries */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-12656">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-12657">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12658">
              Upcoming Deliveries
            </h3>
            <button onClick={() => setCurrentView('receipting')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-12659">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-12660">
            {upcomingDeliveries.map(delivery => <li key={delivery.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('receipting')} data-id="element-12661">
                <div className="flex items-center justify-between" data-id="element-12662">
                  <div className="flex items-center" data-id="element-12663">
                    <div className="flex-shrink-0" data-id="element-12664">
                      <TruckIcon className="h-5 w-5 text-blue-400" data-id="element-12665" />
                    </div>
                    <div className="ml-3" data-id="element-12666">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-12667">
                        {delivery.items}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-12668">
                        {delivery.id} • {delivery.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-12669">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${delivery.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-12670">
                      {delivery.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-12671">
                      Expected: {delivery.expectedDate}
                    </span>
                  </div>
                </div>
                {delivery.trackingNumber && <div className="mt-2 text-xs text-gray-500" data-id="element-12672">
                    Tracking: {delivery.trackingNumber}
                  </div>}
              </li>)}
          </ul>
        </div>
      </div>
      {/* Spend by Category */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-12673">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-12674">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12675">
            Spend by Category
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-12676">
          <div className="space-y-4" data-id="element-12677">
            {topSpendCategories.map(category => <div key={category.category} data-id="element-12678">
                <div className="flex items-center justify-between mb-1" data-id="element-12679">
                  <div className="flex items-center" data-id="element-12680">
                    <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-12681" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-12682">
                      {category.category}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-12683">
                    {formatCurrency(category.spend, 'USD', locale)} (
                    {category.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-12684">
                  <div className="bg-indigo-600 h-2.5 rounded-full" style={{
                width: `${category.percentage}%`
              }} data-id="element-12685"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-12686">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-12687">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-12688">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12689">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-12690">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-12691">
            {recentRequisitions.map(req => <li key={req.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('requisitions')} data-id="element-12692">
                <div className="flex items-center justify-between" data-id="element-12693">
                  <div className="flex items-center" data-id="element-12694">
                    <div className="flex-shrink-0" data-id="element-12695">
                      <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-12696" />
                    </div>
                    <div className="ml-3" data-id="element-12697">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-12698">
                        {req.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-12699">
                        {req.id} • {req.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-12700">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.status === 'Approved' ? 'bg-green-100 text-green-800' : req.status === 'Draft' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-12701">
                      {req.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-12702">
                      {formatCurrency(req.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-12703">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-12704">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12705">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-12706">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-12707">
            {recentPurchaseOrders.map(po => <li key={po.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('purchase-orders')} data-id="element-12708">
                <div className="flex items-center justify-between" data-id="element-12709">
                  <div className="flex items-center" data-id="element-12710">
                    <div className="flex-shrink-0" data-id="element-12711">
                      <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-12712" />
                    </div>
                    <div className="ml-3" data-id="element-12713">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-12714">
                        {po.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-12715">
                        {po.id} • {po.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-12716">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.status === 'Sent to Vendor' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-12717">
                      {po.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-12718">
                      {formatCurrency(po.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </div>
      {/* Procurement KPIs */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-12719">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-12720">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12721">
            Procurement KPIs
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-12722">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-12723">
            <div className="bg-green-50 p-4 rounded-lg" data-id="element-12724">
              <div className="flex items-center" data-id="element-12725">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-2" data-id="element-12726">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-12727" />
                </div>
                <div className="ml-4" data-id="element-12728">
                  <h4 className="text-sm font-medium text-green-900" data-id="element-12729">
                    On-Time Delivery
                  </h4>
                  <p className="text-3xl font-semibold text-green-700" data-id="element-12730">92%</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-12731">
              <div className="flex items-center" data-id="element-12732">
                <div className="flex-shrink-0 bg-blue-100 rounded-md p-2" data-id="element-12733">
                  <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-12734" />
                </div>
                <div className="ml-4" data-id="element-12735">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-12736">
                    Avg. Processing Time
                  </h4>
                  <p className="text-3xl font-semibold text-blue-700" data-id="element-12737">
                    3.2 days
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg" data-id="element-12738">
              <div className="flex items-center" data-id="element-12739">
                <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2" data-id="element-12740">
                  <PackageIcon className="h-6 w-6 text-indigo-600" data-id="element-12741" />
                </div>
                <div className="ml-4" data-id="element-12742">
                  <h4 className="text-sm font-medium text-indigo-900" data-id="element-12743">
                    Order Accuracy
                  </h4>
                  <p className="text-3xl font-semibold text-indigo-700" data-id="element-12744">98%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}